
import { defineComponent, h, ref } from 'vue';
import { FormInst, FormItemRule } from 'naive-ui';
import { toast, notification } from '../utils/notification.util';
import HoundSelectOption from './shared/HoundSelectOption.vue';
import { SelectOption } from 'naive-ui/lib/select/src/interface';
import BaseNotificationContent from './shared/BaseNotificationContent.vue';
import { http } from '../services';
import { HoundModel, FindAllResponse, formatNumberDecimals, emptyAddress } from '../../../common';
import { MicroPayment } from '../../../common/ethers-v5/Queues';
import { EthersService as eth, convertToUSD } from '../services';
import { breedingPlatformFee, breedingTransactionCost, exchangeRatesAvailable, exchangeRates, externalBreedingFeeCurrency, breedingTransactionCostCurrency } from '@/data/global';
import { BigNumber } from 'ethers';
import { getCurrencyLabel } from '../../../common/inventories/index';

export default defineComponent({
  emits: ['cancel', 'mountHound'],
  props: {
    houndId: {
      type: Number,
      required: true
    },
    houndTokenId: {
      type: String,
      required: true
    },
    breedingFee: {
      type: String,
      required: true
    }
  },
  setup(props, { emit }) {
    const formRef = ref<FormInst | null>(null);
    const cancel = () => {
      emit('cancel');
    };

    const formValue = ref({
      houndId: null
    });

    const hound = ref<HoundModel>();
    const houndOptions = ref<SelectOption[]>();
    const hounds = ref<HoundModel[]>([]);

    const loading = ref<boolean>(false);

    const currencyName = eth.currenciesInfo.find(c => c.value === externalBreedingFeeCurrency.value)?.label;
    const transactionCurrencyName = eth.currenciesInfo.find(c => c.value === breedingTransactionCostCurrency.value)?.label;
    const currencyId = eth.currenciesInfo.find(c => c.value === externalBreedingFeeCurrency.value)?.value;
    
    http.getHound(String(props.houndId)).then((res: HoundModel) => {
      hound.value = new HoundModel(res);

      http.getOwnHounds({
        limit: null,
        offset: null,
        sex: hound.value.sex === 'Male' ? 2 : 1,
        running: false,
        hasLibido: true
      }).then((res: FindAllResponse<HoundModel>) => {
        res.data.forEach(el => {
          hounds.value.push(new HoundModel(el));
        })
        houndOptions.value = res.data.map((h: HoundModel) => ({
          value: h.token_id,
          label: h.name
        }));
      });
    });

    const renderLabel = (option: SelectOption) => {
      const hound = hounds.value.find(h => h.token_id === option.value);
      return h(
        HoundSelectOption,
        {
          hound: hound,
          style: 'height: 5rem'
        }
      )
    };

    const renderTag = ({ option }) => {
      const hound = hounds.value.find(h => h.token_id === option.value);
      return h(
        HoundSelectOption,
        {
          hound: hound,
          style: 'height: 5rem',
          key: hound.id
        }
      );
    };

    const breed = () => {
      formRef.value?.validate(async (errors) => {
        if (errors) {
          toast.error('Invalid values');
          return;
        }
        
        try {
          loading.value = true;
          const breedCost: [MicroPayment.StructStructOutput, MicroPayment.StructStructOutput, MicroPayment.StructStructOutput] = await eth.hounds.getBreedCosts(
            String(props.houndTokenId),{
              from: eth.userAddress
            }
          );

          let totalDefaultCurrencyToPay: BigNumber = BigNumber.from(0);
          let customApprovalsToMake: Array<{ currency: string, amount: string | BigNumber }> = [];

          for ( let i = 0 , l = breedCost.length ; i < l ; ++i ) {
            if ( breedCost[i].currency === emptyAddress ) {
              totalDefaultCurrencyToPay = totalDefaultCurrencyToPay.add(breedCost[i].amount);
            } else {
              let exists = -1;
              for ( let j = 0 , k = customApprovalsToMake.length ; j < k ; ++j ) {
                if ( customApprovalsToMake[j].currency === breedCost[i].currency ) {
                  exists = j;
                  break;
                }
              }
              if ( exists === -1 ) {
                customApprovalsToMake.push({
                  currency: breedCost[i].currency,
                  amount: breedCost[i].amount
                });
              } else {
                customApprovalsToMake[exists].amount = BigNumber.from(customApprovalsToMake[exists].amount).add(breedCost[i].amount);
              }
            }
          }

          for ( let i = 0 , l = customApprovalsToMake.length ; i < l ; ++i ) {
            const customTokenInterface = eth.getCustomTokenInterface(customApprovalsToMake[i].currency);
            const alreadyApproved = await customTokenInterface.allowance(eth.userAddress, eth.contractAddresses.PAYMENTS);
              if (alreadyApproved.lt(customApprovalsToMake[i].amount)) {
                const approveTx = await eth.getCustomTokenInterface(
                  customApprovalsToMake[i].currency
                ).approve(
                  eth.contractAddresses.PAYMENTS, 
                  BigNumber.from("1000000000000000000").gt(customApprovalsToMake[i].amount) ? "1000000000000000000" : customApprovalsToMake[i].amount
                );
                await approveTx.wait();
              }
          }

          const tx = await eth.hounds.breedHounds(
            String(formValue.value.houndId),
            String(props.houndTokenId), {
              value: totalDefaultCurrencyToPay,
              gasLimit: 3000000
            }
          );
          
          await tx.wait();

          const distinctCosts: Array<{ amount: BigNumber, currency: string }> = [];
          breedCost.forEach(bc => {
            const bcf = { amount: bc.amount, currency: bc.currency };
            const existingCost = distinctCosts.find(dc => dc.currency === bc.currency);
            if (existingCost) existingCost.amount = BigNumber.from(existingCost.amount).add(bc.amount);
            else distinctCosts.push(bcf);
          })
          const ownHound = hounds.value.find(h => h.token_id === formValue.value.houndId);
          notification.create({
            title: 'Hounds Bred',
            description: `You've successfully mounted ${hound.value.name} with ${ownHound.name}.`,
            content: () => h(
              BaseNotificationContent,
              {
                infoElements: [
                  { label: 'Marketplace Hound', content: hound.value.name },
                  { label: 'Your Hound', content: ownHound.name },
                  {
                    label: 'Breeding Fee',
                    content: distinctCosts
                      .map(cost => {
                        if (cost.amount.gt(0)) {
                          return `${eth.utils.formatEther(cost.amount)} ${getCurrencyLabel(eth.chainId, cost.currency)}`
                        }
                      })
                      .join(' | ')
                  }
                ]
              }
            ),
            meta: new Date().toUTCString()
          });
          emit('mountHound');
          toast.success('Successful breed');
        } catch(err) {
          toast.error('Transaction Failed');
          console.log(err);
        } finally {
          loading.value = false;
          cancel();
        }
      })
    };

    const rules = {
      houndId: {
        trigger: ['blur', 'input'],
        validator: (rule: FormItemRule, value: any) => {
          return new Promise<void>((resolve, reject) => {
            if (value === null) reject(Error('Hound is required'));
            resolve();
          });
        }
      }
    }

    return {
      formRef,
      formValue,
      cancel,
      loading,
      breed,
      rules,
      houndOptions,
      hound,
      breedingFee: props.breedingFee,
      renderLabel,
      renderTag,
      eth,
      platformFee: breedingPlatformFee,
      transactionCost: breedingTransactionCost,
      formatNumberDecimals,
      currencyName,
      transactionCurrencyName,
      currencyId,
      convertToUSD,
      exchangeRatesAvailable,
      exchangeRates
    };
  },
})
